import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Switch, Route, Link, matchPath } from 'react-router-dom'
import flowSelectors from '../selectors/flowSelector'
import { DisplayText, TextContainer, TextField, Button, Stack, Spinner, ErrorText, Hero } from './../components'
import styles from './../assets/scss/views/Profile.scss'
import * as yup from 'yup'

import Profile from './Profile/Profile'
import Account from './Profile/Account'
import History from './Profile/History'
import Privacy from './Profile/Privacy'

import * as translationService from '../services/translationService'
import * as networkService from './../services/networkService'
import * as routeService from './../services/routeService'
import { getApplicationProfile, resetFlow } from '../actions/FlowAction'
import { getUserInfo } from '../actions/AuthAction'

// const StyledTabLink = styled(Link)`
// eslint-disable-next-line
const StyledTabLink = styled(({ isActive, ...rest }) => <Link {...rest} />)`
  white-space: nowrap;
  &:hover,
  &:focus {
    text-decoration: none;
    color: inherit;
  }
  ${({ isActive, theme }) =>
    isActive &&
    `&& {
        color: ${theme.primaryColor};
        @media (min-width: 40em) {
          border-bottom-color: ${theme.primaryColor};
        }
      }`};
`

const ProfileView = ({ user, application, ...props }) => {
  const isBroker = !!user && user.activeAccountId !== user.personalAccountId

  const [pdfState, setPdfState] = React.useState({})
  const [email, setEmail] = React.useState(user.email || '')
  const [fieldErrors, setFieldErrors] = React.useState(null)
  const [mainError, setMainError] = React.useState(null)

  React.useEffect(() => {
    props.resetFlow()
    props.getApplicationProfile(user)
    props.getUserInfo(user.id, user.accessToken)
  }, [])

  const sendPdf = async () => {
    const pdfSuccess = translationService.t('history.mail.sended')
    const pdfFail = translationService.t('profile.error.pdf.send')

    const emailSchema = yup.string().email()

    const isValidEmail = await emailSchema.isValid(email) // => true
    if (!isValidEmail) {
      setFieldErrors({ email: true })
      setMainError(translationService.t('system.form.error.invalid_email'))

      return
    }

    try {
      setPdfState({ sending: true })
      await networkService.sendPdf(user.id, email)
      setPdfState({ text: pdfSuccess })
      setFieldErrors(null)
      setMainError(null)
    } catch (err) {
      console.log({ err })

      const {
        response: {
          data: { error },
        },
      } = err
      setPdfState({ sending: false })

      if (error && error.code === 'EMAIL_NOT_VERIFIED') {
        setFieldErrors({ email: true })
        setMainError(translationService.t('system.error.EMAIL_NOT_VERIFIED'))
      } else {
        setFieldErrors({})
        setMainError(pdfFail)
      }
    }
  }

  const resetPdfForm = () => {
    setPdfState({})
  }

  const handleEmailChange = ({ target: { value } }) => {
    setEmail(value)
  }

  const renderAccount = () => (
    <TextContainer>
      <DisplayText>{translationService.myAccount()}</DisplayText>
      <Account />
    </TextContainer>
  )

  const renderHistory = () => {
    if (isBroker) {
      return null
    }

    return (
      <TextContainer>
        <DisplayText>{translationService.mySubmissionHistory()}</DisplayText>
        <History />
      </TextContainer>
    )
  }

  const renderPrivacy = () => (
    <TextContainer>
      {/* <DisplayText>{translationService.accountPrivacyTitle()}</DisplayText> */}
      <Privacy />
    </TextContainer>
  )

  const renderProfile = () => (
    <TextContainer>
      <DisplayText>{translationService.myRentProfile()}</DisplayText>

      <Profile checkValidity={false} />
      {pdfState && pdfState.text ? (
        <Hero>
          {pdfState.text}
          &nbsp;
          <a href="#" onClick={resetPdfForm}>
            {translationService.t('system.send.again')}
          </a>
        </Hero>
      ) : (
        <TextContainer>
          <DisplayText size="extraSmall">{translationService.sendPdfToEmail()}</DisplayText>
          <div>
            <Stack alignment="trailing">
              <Stack.Item fill>
                <TextField
                  id="email"
                  name="email"
                  label={translationService.emailadres()}
                  value={email}
                  onChange={handleEmailChange}
                  error={fieldErrors && fieldErrors.email}
                />
              </Stack.Item>
              <Stack.Item>
                <Button disabled={pdfState.sending} onClick={sendPdf} primary>
                  {translationService.sendEmailToButtonText()}
                </Button>
              </Stack.Item>
            </Stack>
          </div>
          {mainError && (
            <div>
              <ErrorText>{mainError}</ErrorText>
              <br />
            </div>
          )}
        </TextContainer>
      )}
    </TextContainer>
  )

  return (
    <React.Fragment>
      {!application.id && <Spinner size="large" />}
      {application.id && (
        <div>
          <div className={styles.TabContainer}>
            <ul className={styles.Tabs}>
              <li>
                <StyledTabLink
                  role="button"
                  to={routeService.getPrefixPath() + '/profiel/'}
                  isActive={!!matchPath(window.location.pathname, { path: '/profiel/', exact: true })}
                  className={styles.Tab}>
                  {translationService.myRentProfile()}
                </StyledTabLink>
              </li>
              {!isBroker && (
                <li>
                  <StyledTabLink
                    role="button"
                    to={routeService.getPrefixPath() + '/profiel/aanvragen'}
                    isActive={!!matchPath(window.location.pathname, { path: '/profiel/aanvragen', exact: true })}
                    className={styles.Tab}>
                    {translationService.mySubmissionHistory()}
                  </StyledTabLink>
                </li>
              )}
              <li>
                <StyledTabLink
                  role="button"
                  to={routeService.getPrefixPath() + '/profiel/account'}
                  isActive={!!matchPath(window.location.pathname, { path: '/profiel/account', exact: true })}
                  className={styles.Tab}>
                  {translationService.myAccount()}
                </StyledTabLink>
              </li>
              {user.tac && (
                <li>
                  <StyledTabLink
                    role="button"
                    to={routeService.getPrefixPath() + '/profiel/privacy'}
                    isActive={!!matchPath(window.location.pathname, { path: '/profiel/privacy', exact: true })}
                    className={styles.Tab}>
                    {translationService.accountPrivacyTitle()}
                  </StyledTabLink>
                </li>
              )}
            </ul>
          </div>
          <Switch>
            <Route exact path="/profiel" render={renderProfile} />
            {!isBroker && <Route exact path="/profiel/aanvragen" component={renderHistory} />}
            <Route exact path="/profiel/account" component={renderAccount} />
            <Route exact path="/profiel/privacy" component={renderPrivacy} />
          </Switch>
        </div>
      )}
    </React.Fragment>
  )
}

ProfileView.propTypes = {
  user: PropTypes.object,
  application: PropTypes.object,
  getApplicationProfile: PropTypes.func,
  getUserInfo: PropTypes.func,
  resetFlow: PropTypes.func,
  id: PropTypes.any,
}

const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {} } = AuthReducer
  return {
    user,
    application: flowSelectors.application(FlowReducer),
  }
}

const mapDispatchToProps = dispatch => ({
  getApplicationProfile: user => dispatch(getApplicationProfile(user)),
  getUserInfo: (userId, token) => dispatch(getUserInfo(userId, token)),
  resetFlow: () => dispatch(resetFlow()),
})

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(ProfileView)



// WEBPACK FOOTER //
// ./src/views/ProfileView.js